import { useBrainStore } from '@/state/brainStore';
import { resendDepositLink, resendExpiredLink, isEffectivelyExpired, AWAITING_CLIENT } from '@/domain/depositFlow';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import type { Booking } from '@/domain/types';

/**
 * Expired-link resend routing (Prompt I1, 14 + 15 Jul rulings). On a resend for an
 * EXPIRED deposit, never guess a date: check whether the originally discussed slot
 * is still free.
 *
 *  - Free  -> re-mint the link for the SAME slot (object-form `resendDepositLink`).
 *  - Taken -> do NOT mint a link. Push the customer into sequence 1 of the
 *             awaiting-deposit (money) queue, carrying the deposit's history + agent
 *             attribution, at the TOP of the money band ("highest priority for the
 *             queue", Jake 15 Jul).
 *
 * Lives in its own file so `depositFlow` (which `sequenceFlow` imports) never has
 * to import `sequenceFlow` back - no import cycle.
 */

export type RouteExpiredResendResult =
  | { outcome: 'resent'; freshDepositId: string }
  | { outcome: 'queued' }
  // Served (W5b #2): handed to the one honest resend intent; it settles async.
  | { outcome: 'requested' };

/** Outcome of the generalised overbook re-call (tv-6d8). */
export type OverbookRecallResult = { outcome: 'recalled' } | { outcome: 'still-held' };

const RESEND_DESC = 'Deposit needs resend - original date is taken, rebook needed';
const RECALL_DESC = 'Held date was taken first - re-call the client to pick a new date';

const DEAD_STATUSES = new Set<Booking['status']>(['cancelled', 'no-show', 'superseded']);

interface Slot {
  artistId: string;
  startIso: string;
  endIso: string;
}

/**
 * Shared "held slot was taken -> re-call for a new date" routing (tv-6d8). Pushes
 * the (already-warm) customer onto the AGENT worklist via a stage-1 sequence
 * record carrying the lost-slot context, so it surfaces as a pushed-lead card and
 * never lands on the Sweeper. Both callers share it: the expired-deposit resend
 * (money cohort, keeps the deposit ref) and the live overbook re-call (fresh,
 * warm, re-entering at build-the-booking).
 */
function pushSlotTakenRecall(opts: {
  customerId: string;
  description: string;
  cohort: 'money' | 'fresh';
  depositId?: string;
}): void {
  const brain = useBrainStore.getState();
  const existing = brain.sequenceState[opts.customerId];
  brain.setSequenceState(opts.customerId, {
    ...existing,
    stage: 1,
    cohort: opts.cohort,
    redKind: undefined,
    nextDueAt: undefined,
    sourceConversationId: existing?.sourceConversationId ?? opts.customerId,
    description: opts.description,
    ...(opts.depositId ? { depositId: opts.depositId } : {}),
  });
}

/**
 * True when a live booking (not the deposit's own ghost hold) already overlaps the
 * slot on the same artist. Pure epoch-overlap - independent of timezone and of the
 * synthetic "Studio time" filler buildDaySchedule injects for display.
 */
function slotTaken(
  slot: Slot,
  bookings: Record<string, Booking>,
  excludeBookingId: string | undefined,
): boolean {
  const s = new Date(slot.startIso).getTime();
  const e = new Date(slot.endIso).getTime();
  return Object.values(bookings).some((b) => {
    if (b.id === excludeBookingId) return false;
    if (b.artistId !== slot.artistId) return false;
    if (DEAD_STATUSES.has(b.status)) return false;
    const bs = new Date(b.start).getTime();
    const be = bs + b.durationMin * 60_000;
    return bs < e && be > s;
  });
}

export function routeExpiredResend(depositId: string): RouteExpiredResendResult | undefined {
  const brain = useBrainStore.getState();
  const deposit = brain.deposits[depositId];
  if (!deposit || !isEffectivelyExpired(deposit)) return undefined;

  // Served (W5b #2, D21): the old path stamped the old link "resent" before the
  // hold-slot intent answered, and a 422 never un-stamped it. Hand it to the one
  // honest resend (mint + supersede in one server transaction); a gone slot is
  // queued for a re-call, as below, since this caller can't pick a date.
  if (!isStoreFallbackEnabled()) {
    void resendExpiredLink({ depositId, automatic: true }).then((out) => {
      if (out.outcome !== 'needs-new-date') return;
      pushSlotTakenRecall({ customerId: deposit.customerId, description: RESEND_DESC, cohort: 'money', depositId: deposit.id });
    });
    return { outcome: 'requested' };
  }

  // Derive the slot: the discussed slot, else the (now-cancelled) held ghost booking.
  const held = deposit.heldBookingId ? brain.bookings[deposit.heldBookingId] : undefined;
  const slot =
    deposit.discussedSlot ??
    (held
      ? {
          artistId: held.artistId,
          startIso: held.start,
          endIso: new Date(new Date(held.start).getTime() + held.durationMin * 60_000).toISOString(),
        }
      : undefined);

  // No slot on record at all: a deposit link always carries a date (diagram 03;
  // Anas 1 Oct, 7g3y), so it can't be re-sent as it is. Queue a call to pick one.
  if (!slot) {
    pushSlotTakenRecall({ customerId: deposit.customerId, description: RESEND_DESC, cohort: 'money', depositId: deposit.id });
    return { outcome: 'queued' };
  }

  if (!slotTaken(slot, brain.bookings, deposit.heldBookingId)) {
    const fresh = resendDepositLink({ depositId, discussedSlot: slot });
    return fresh ? { outcome: 'resent', freshDepositId: fresh.id } : undefined;
  }

  // Taken: route back to the money queue, carrying context + attribution, via the
  // shared re-call helper (also used by the live overbook re-call, tv-6d8).
  pushSlotTakenRecall({
    customerId: deposit.customerId,
    description: RESEND_DESC,
    cohort: 'money',
    depositId: deposit.id,
  });
  return { outcome: 'queued' };
}

/**
 * Generalised locked-date / overbook re-call (tv-6d8, Diagram 05). When a date a
 * client locked (a `held` booking) is taken first by a live booking overlapping
 * the same artist/slot (a this-week overbook), void the now-dead hold and re-call
 * the (already-warm) client to pick a new date - re-entering at build-the-booking
 * on the AGENT worklist, never the Sweeper. Independent of deposit-link expiry;
 * shares the slot-taken check + re-call routing with routeExpiredResend.
 *
 * Returns 'recalled' when the slot was taken and the client was queued, or
 * 'still-held' when the hold is intact. `undefined` for an unknown booking, a
 * non-`held` booking, or a self-book hold with no client yet (AWAITING_CLIENT).
 */
export function routeOverbookRecall(heldBookingId: string): OverbookRecallResult | undefined {
  const brain = useBrainStore.getState();
  const held = brain.bookings[heldBookingId];
  if (!held || held.status !== 'held') return undefined;
  // A universal-link hold has no client to re-call yet.
  if (held.customerId === AWAITING_CLIENT) return undefined;

  const slot: Slot = {
    artistId: held.artistId,
    startIso: held.start,
    endIso: new Date(new Date(held.start).getTime() + held.durationMin * 60_000).toISOString(),
  };
  if (!slotTaken(slot, brain.bookings, held.id)) return { outcome: 'still-held' };

  // Void the dead hold so the phantom slot doesn't linger, then re-call the client
  // warm. Fresh cohort (not money) keeps it firmly on the Agent, never the Sweeper.
  brain.upsertBooking({ ...held, status: 'cancelled' });
  pushSlotTakenRecall({ customerId: held.customerId, description: RECALL_DESC, cohort: 'fresh' });
  return { outcome: 'recalled' };
}

/**
 * The "someone paid first" hook (tv-nid, Diagram 02). Once a booking locks a slot,
 * every OTHER overbookable `held` ghost overlapping that slot on the same artist
 * has lost the race: route each through the existing `routeOverbookRecall`, which
 * sees the freshly-locked booking as the live overlap, voids the dead hold and
 * re-calls the client warm onto the Agent worklist. Only `overbookable` holds are
 * swept - firm 24h Sweeper gap-fill holds are never overbooked. No-op for an
 * unknown booking. Does not itself mutate the store; it delegates to
 * `routeOverbookRecall`, so the recall logic lives in exactly one place.
 */
export function recallOverlappingHolds(lockedBookingId: string): void {
  // Served (D02-07 / D05-19): the pay's own server transaction already released
  // the losing holds (released_reason 'slot-taken') and the Agent's "Date taken"
  // alert reads that state. A second, client-side release here would race it and
  // push a duplicate card. Store fallback (no server) keeps the local rule.
  if (!isStoreFallbackEnabled()) return;
  const brain = useBrainStore.getState();
  const locked = brain.bookings[lockedBookingId];
  if (!locked) return;
  const ls = new Date(locked.start).getTime();
  const le = ls + locked.durationMin * 60_000;
  for (const b of Object.values(brain.bookings)) {
    if (b.id === lockedBookingId) continue;
    if (b.status !== 'held' || !b.overbookable) continue;
    if (b.artistId !== locked.artistId) continue;
    const bs = new Date(b.start).getTime();
    const be = bs + b.durationMin * 60_000;
    if (bs < le && be > ls) routeOverbookRecall(b.id);
  }
}
